import { useMemo, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Checkbox from '@cloudscape-design/components/checkbox';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Popover from '@cloudscape-design/components/popover';
import Icon from '@cloudscape-design/components/icon';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import { InvisibleCharactersTextDisplay } from './InvisibleCharactersTextDisplay';
import { useClipboard } from '../hooks';
import type { DetectedCharacter } from '../utils/invisibleCharacters';
import type { SecurityAnalysis, ThreatLevel } from '../types';
import { ThreatSummaryDashboard } from './ThreatSummaryDashboard';
import { LineByLineThreatBreakdown } from './LineByLineThreatBreakdown';
import { formatUnicodeCode } from '@utils/invisibleCharacterAnalyzer';
import { analyzeBinaryPattern } from '@utils/invisibleCharacters';
import { formatThreats } from '@utils/threatFormatter';

export interface InvisibleCharacterAnalysisProps {
  // Required
  text: string;
  detectedChars: DetectedCharacter[];
  
  // Optional - Display Configuration
  title?: string;
  description?: string;
  showLineNumbers?: boolean;
  
  // Optional - Feature Toggles
  showSpacesControl?: boolean;
  showUnicodeControl?: boolean;
  showActionButtons?: boolean;
  allowCopyCharacter?: boolean;
  
  // Optional - Security Analysis
  securityAnalysis?: SecurityAnalysis | null;
  
  // Optional - State Management (for external control)
  showSpaces?: boolean;
  showUnicode?: boolean;
  onShowSpacesChange?: (value: boolean) => void;
  onShowUnicodeChange?: (value: boolean) => void;
  
  // Optional - Callbacks
  onRemoveInvisible?: () => void;
  onCopyCleaned?: () => void;
  onCharacterClick?: (position: number) => void;  // Called when a position link is clicked to highlight that character
  onCharacterFix?: (charCode: number) => void;
  
  // Optional - Advanced
  highlightedPosition?: number | null;
}

export const InvisibleCharacterAnalysis = ({
  text,
  detectedChars,
  title = 'Analysis Results',
  description = 'Analyze your text for invisible characters and security patterns',
  showLineNumbers = true,
  showSpacesControl = true,
  showUnicodeControl = true,
  showActionButtons = true,
  allowCopyCharacter = true,
  securityAnalysis = null,
  showSpaces: externalShowSpaces,
  showUnicode: externalShowUnicode,
  onShowSpacesChange,
  onShowUnicodeChange,
  onRemoveInvisible,
  onCopyCleaned,
  onCharacterClick,
  onCharacterFix,
  highlightedPosition = null,
}: InvisibleCharacterAnalysisProps) => {
  // Internal state for uncontrolled mode
  const [internalShowSpaces, setInternalShowSpaces] = useState(false);
  const [internalShowUnicode, setInternalShowUnicode] = useState(false);
  
  const { copyToClipboard } = useClipboard();
  
  // Use external state if provided, otherwise use internal state
  const showSpaces = externalShowSpaces !== undefined ? externalShowSpaces : internalShowSpaces;
  const showUnicode = externalShowUnicode !== undefined ? externalShowUnicode : internalShowUnicode;
  
  const handleShowSpacesChange = (checked: boolean) => {
    if (onShowSpacesChange) {
      onShowSpacesChange(checked);
    } else {
      setInternalShowSpaces(checked);
    }
  };
  
  const handleShowUnicodeChange = (checked: boolean) => {
    if (onShowUnicodeChange) {
      onShowUnicodeChange(checked);
    } else {
      setInternalShowUnicode(checked);
    }
  };
  
  // Calculate statistics
  // Calculate binary pattern from text if we have steganography
  const binaryPattern = useMemo(() => {
    if (securityAnalysis?.hasSteganography && text) {
      return analyzeBinaryPattern(text);
    }
    return null;
  }, [securityAnalysis, text]);

  const statistics = useMemo(() => {
    const totalChars = text.length;
    const invisibleChars = detectedChars.length;
    const visibleChars = totalChars - invisibleChars;
    const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
    
    // Count regular spaces separately (U+0020)
    const spaceCount = (text.match(/\x20/g) || []).length;
    const nonSpaceInvisibleChars = detectedChars.filter(d => d.character.code !== 0x0020).length;
    
    // Group detected characters by type
    const charGroups = new Map<string, { char: typeof detectedChars[0], count: number, positions: number[] }>();
    detectedChars.forEach(detected => {
      const key = `${detected.character.code}`;
      if (charGroups.has(key)) {
        const group = charGroups.get(key)!;
        group.count++;
        group.positions.push(detected.position);
      } else {
        charGroups.set(key, { char: detected, count: 1, positions: [detected.position] });
      }
    });
    
    return {
      totalChars,
      invisibleChars,
      visibleChars,
      wordCount,
      spaceCount,
      nonSpaceInvisibleChars,
      charGroups: Array.from(charGroups.values()).sort((a, b) => b.count - a.count)
    };
  }, [text, detectedChars]);
  
  // Filter out issues that have dedicated detail sections
  const generalSecurityIssues = useMemo(() => {
    if (!securityAnalysis || !securityAnalysis.issues) return [];
    
    const detailedIssues = [
      'steganography_pattern',
      'normalization_instability',
      'bidi_override',
      'homoglyph_abuse',
      'script_mixing',
      'whitespace_abuse',
      'width_abuse',
      'lookalike_digits',
      'quotation_confusion',
      'frequency_anomaly',
    ];
    
    return securityAnalysis.issues.filter(
      issue => !detailedIssues.includes(issue.type)
    );
  }, [securityAnalysis]);
  
  const handleCopyCharacter = (charCode: number) => {
    const char = String.fromCharCode(charCode);
    copyToClipboard(char);
  };
  
  const handleFixCharacter = (charCode: number) => {
    if (onCharacterFix) {
      onCharacterFix(charCode);
    }
  };
  
  const getCharacterBadgeColor = (category: string): 'green' | 'red' | 'blue' | 'grey' => {
    switch (category) {
      case 'zero-width':
        return 'red';
      case 'whitespace':
        return 'blue';
      case 'directional':
        return 'red';
      case 'format':
        return 'green';
      default:
        return 'grey';
    }
  };
  
  const getThreatLevelBadgeColor = (level: ThreatLevel): 'green' | 'blue' | 'red' => {
    switch (level) {
      case 'LOW': return 'green';
      case 'MEDIUM': return 'blue';
      case 'HIGH': return 'red';
      case 'CRITICAL': return 'red';
    }
  };

  // Helper function to convert character position to line number
  const getLineNumberFromPosition = (position: number): number => {
    if (!text) return 1;
    let lineNumber = 1;
    for (let i = 0; i < position && i < text.length; i++) {
      if (text[i] === '\n') {
        lineNumber++;
      }
    }
    return lineNumber;
  };

  // Helper function to format position with line number and position within line
  const formatPositionWithLine = (position: number): string => {
    const lineNum = getLineNumberFromPosition(position);
    
    // Calculate position within the line
    let lineStartPos = 0;
    for (let i = 0; i < position && i < text.length; i++) {
      if (text[i] === '\n') {
        lineStartPos = i + 1;
      }
    }
    const posInLine = position - lineStartPos + 1; // +1 for 1-based indexing
    
    return `Line ${lineNum}:${posInLine} (Pos ${position})`;
  };

  // Helper function to create clickable position display
  const renderClickablePosition = (position: number) => {
    const positionText = formatPositionWithLine(position);
    return (
      <span
        onClick={() => {
          // Trigger highlighting by calling onCharacterClick with the position
          if (onCharacterClick) {
            onCharacterClick(position);
          }
        }}
        style={{
          color: '#0073bb',
          textDecoration: 'underline',
          cursor: onCharacterClick ? 'pointer' : 'default',
        }}
      >
        {positionText}
      </span>
    );
  };
  
  return (
    <Container
      header={
        <Header variant="h2" description={description}>
          {title}
        </Header>
      }
    >
      <ColumnLayout columns={2} variant="text-grid" minColumnWidth={400}>
        {/* Left: Invisible Characters Revealed */}
        <div>
          <SpaceBetween size="l">
            <Header
              variant="h3"
              description={
                showSpaces 
                  ? `${detectedChars.length} invisible character${detectedChars.length !== 1 ? 's' : ''} detected (including ${statistics.spaceCount} space${statistics.spaceCount !== 1 ? 's' : ''})`
                  : statistics.spaceCount > 0
                  ? `${statistics.nonSpaceInvisibleChars} invisible character${statistics.nonSpaceInvisibleChars !== 1 ? 's' : ''} detected (+ ${statistics.spaceCount} space${statistics.spaceCount !== 1 ? 's' : ''} hidden)`
                  : `${detectedChars.length} invisible character${detectedChars.length !== 1 ? 's' : ''} detected`
              }
            >
              Invisible Characters Revealed
            </Header>

            {detectedChars.length > 0 ? (
              <>
                <SpaceBetween size="xs" direction="horizontal">
                  {showSpacesControl && (
                    <Checkbox
                      checked={showSpaces}
                      onChange={({ detail }) => handleShowSpacesChange(detail.checked)}
                    >
                      Show Spaces
                    </Checkbox>
                  )}
                  {showUnicodeControl && (
                    <Checkbox
                      checked={showUnicode}
                      onChange={({ detail }) => handleShowUnicodeChange(detail.checked)}
                    >
                      Show Unicode
                    </Checkbox>
                  )}
                </SpaceBetween>

                <InvisibleCharactersTextDisplay
                  text={text}
                  detectedChars={detectedChars}
                  showLineNumbers={showLineNumbers}
                  showSpaces={showSpaces}
                  showUnicode={showUnicode}
                  highlightedPosition={highlightedPosition}
                  onCharacterClick={allowCopyCharacter ? handleCopyCharacter : undefined}
                  showActionButtons={showActionButtons}
                  onRemoveInvisible={onRemoveInvisible}
                  onCopyCleaned={onCopyCleaned}
                />
              </>
            ) : (
              <Alert type="success">
                No invisible characters detected in your text.
              </Alert>
            )}
          </SpaceBetween>
        </div>

        {/* Right: Character Analysis */}
        <div>
          <SpaceBetween size="l">
            <Header variant="h3">
              Character Analysis
            </Header>

            {detectedChars.length > 0 ? (
              <SpaceBetween size="l">
                {/* Statistics Grid */}
                <ColumnLayout columns={2} variant="text-grid">
                  <div>
                    <SpaceBetween size="xs">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box variant="awsui-key-label" display="inline">"Visible" Characters</Box>
                        <Popover
                          dismissButton={false}
                          position="top"
                          size="small"
                          triggerType="custom"
                          content={
                            <Box variant="p">
                              Characters that are visible and printable in the text.
                            </Box>
                          }
                        >
                          <Icon name="status-info" variant="subtle" />
                        </Popover>
                      </div>
                      <Box variant="h1" fontSize="display-l" color="text-status-success">
                        {statistics.visibleChars}
                      </Box>
                    </SpaceBetween>
                  </div>

                  <div>
                    <SpaceBetween size="xs">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box variant="awsui-key-label" display="inline">Invisible Characters</Box>
                        <Popover
                          dismissButton={false}
                          position="top"
                          size="small"
                          triggerType="custom"
                          content={
                            <Box variant="p">
                              Hidden characters that don't display visually but are present in the text.
                            </Box>
                          }
                        >
                          <Icon name="status-info" variant="subtle" />
                        </Popover>
                      </div>
                      <Box variant="h1" fontSize="display-l" color="text-status-error">
                        {statistics.invisibleChars}
                      </Box>
                    </SpaceBetween>
                  </div>

                  <div>
                    <SpaceBetween size="xs">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box variant="awsui-key-label" display="inline"># of Characters</Box>
                        <Popover
                          dismissButton={false}
                          position="top"
                          size="small"
                          triggerType="custom"
                          content={
                            <Box variant="p">
                              Total number of characters including both visible and invisible.
                            </Box>
                          }
                        >
                          <Icon name="status-info" variant="subtle" />
                        </Popover>
                      </div>
                      <Box variant="h1" fontSize="display-l">
                        {statistics.totalChars}
                      </Box>
                    </SpaceBetween>
                  </div>

                  <div>
                    <SpaceBetween size="xs">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Box variant="awsui-key-label" display="inline"># of Words</Box>
                        <Popover
                          dismissButton={false}
                          position="top"
                          size="small"
                          triggerType="custom"
                          content={
                            <Box variant="p">
                              Number of words in the text, separated by whitespace.
                            </Box>
                          }
                        >
                          <Icon name="status-info" variant="subtle" />
                        </Popover>
                      </div>
                      <Box variant="h1" fontSize="display-l">
                        {statistics.wordCount}
                      </Box>
                    </SpaceBetween>
                  </div>
                </ColumnLayout>

                {/* Security Risk Assessment */}
                {securityAnalysis && (
                  <SpaceBetween size="m">
                    <Box variant="h3">Security Risk Assessment</Box>
                    
                    {/* Risk Score Overview */}
                    <SpaceBetween size="xs">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <Box variant="awsui-key-label">Risk Score</Box>
                        <Badge color={getThreatLevelBadgeColor(securityAnalysis.threatLevel)}>
                          {securityAnalysis.threatLevel} RISK
                        </Badge>
                      </div>
                      <ProgressBar
                        value={securityAnalysis.score}
                        resultButtonText={`${securityAnalysis.score}/100 - ${securityAnalysis.threatLevel} RISK`}
                      />
                    </SpaceBetween>

                    {/* General Security Issues - New Organized Threat Display */}
                    {generalSecurityIssues.length > 0 && (() => {
                      const threatBreakdown = formatThreats(generalSecurityIssues, text);
                      
                      const handleThreatPositionClick = (position: number) => {
                        if (onCharacterClick) {
                          onCharacterClick(position);
                        }
                      };

                      return (
                        <SpaceBetween size="m">
                          <ThreatSummaryDashboard
                            threatGroups={threatBreakdown.groups}
                          />
                          <LineByLineThreatBreakdown
                            lineData={threatBreakdown.lineBreakdown}
                            onPositionClick={handleThreatPositionClick}
                          />
                        </SpaceBetween>
                      );
                    })()}

                    {/* Detailed Pattern Analysis */}
                    <SpaceBetween size="s">
                      {/* ===== DETAILED PATTERN ANALYSIS ===== */}
                      {(securityAnalysis?.bidiExploit || 
                        securityAnalysis?.homoglyphs?.length || 
                        securityAnalysis?.hasSteganography ||
                        securityAnalysis?.normalization ||
                        securityAnalysis?.whitespaceAbuse ||
                        securityAnalysis?.widthAbuse ||
                        securityAnalysis?.lookalikeDigits ||
                        securityAnalysis?.quotationConfusion ||
                        securityAnalysis?.repeatingPattern ||
                        securityAnalysis?.positionalPattern ||
                        securityAnalysis?.diacriticsAbuse ||
                        securityAnalysis?.formatAbuse ||
                        securityAnalysis?.semanticContext ||
                        securityAnalysis?.urlConfusables ||
                        securityAnalysis?.zwChain ||
                        securityAnalysis?.frequencyAnalysis ||
                        securityAnalysis?.controlExploit ||
                        securityAnalysis?.puaDetection?.hasPUA ||
                        securityAnalysis?.fullwidthExploitation?.hasFullwidth ||
                        securityAnalysis?.tagBlockAnomalies?.hasTagBlockChars ||
                        securityAnalysis?.emojiTokenAnalysis?.hasComplexEmoji
                      ) && (
                        <div style={{ borderBottom: '2px solid #7b1fa2', paddingBottom: '8px', marginBottom: '16px' }}>
                          <Box variant="h2">Detailed Pattern Analysis</Box>
                        </div>
                      )}

                      {/* Steganography Detection */}
                      {securityAnalysis.hasSteganography && securityAnalysis.steganographyDetails && (
                        <ExpandableSection headerText={`Steganography Detected (Potential AI Watermark) - ${securityAnalysis.steganographyDetails.confidence}% confidence`} variant="container">
                          <Alert type={securityAnalysis.steganographyDetails.confidence >= 70 ? 'error' : 'warning'}>
                            <SpaceBetween size="s">
                              <Box variant="p">{securityAnalysis.steganographyDetails.details}</Box>
                              <ColumnLayout columns={2} variant="text-grid">
                                <div>
                                  <Box variant="awsui-key-label">Pattern Type</Box>
                                  <Box variant="p">
                                    {securityAnalysis.steganographyDetails.type.replace(/_/g, ' ').toUpperCase()}
                                  </Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Confidence</Box>
                                  <Box variant="p">{securityAnalysis.steganographyDetails.confidence}%</Box>
                                </div>
                              </ColumnLayout>
                              {binaryPattern && (
                                <ColumnLayout columns={2} variant="text-grid">
                                  <div>
                                    <Box variant="awsui-key-label">Binary Pattern</Box>
                                    <div style={{ fontFamily: 'monospace', fontSize: '12px', wordBreak: 'break-all' }}>
                                      {binaryPattern.binaryString}
                                    </div>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Hex Value</Box>
                                    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{binaryPattern.hexValue}</div>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Decimal Value</Box>
                                    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{binaryPattern.decimalValue}</div>
                                  </div>
                                  <div>
                                    <Box variant="awsui-key-label">Pattern Length</Box>
                                    <div style={{ fontFamily: 'monospace', fontSize: '12px' }}>{binaryPattern.patternLength} bits</div>
                                  </div>
                                </ColumnLayout>
                              )}
                              {securityAnalysis.steganographyDetails.decodedData && (
                                <div>
                                  <Box variant="awsui-key-label">Decoded Data</Box>
                                  <div
                                    style={{
                                      fontFamily: 'monospace',
                                      fontSize: '14px',
                                      padding: '8px',
                                      backgroundColor: '#f5f5f5',
                                      borderRadius: '4px',
                                      wordBreak: 'break-all',
                                    }}
                                  >
                                    {securityAnalysis.steganographyDetails.decodedData}
                                  </div>
                                </div>
                              )}
                            </SpaceBetween>
                          </Alert>
                        </ExpandableSection>
                      )}

                      {/* Unicode Normalization Instability */}
                      {securityAnalysis.normalization && securityAnalysis.normalization.isUnstable && (
                        <ExpandableSection headerText="Unicode Normalization Instability" variant="container">
                          <SpaceBetween size="m">
                            <Alert type="info">
                              <SpaceBetween size="xs">
                                <Box variant="strong">What is Unicode Normalization?</Box>
                                <Box variant="p">
                                  Unicode allows multiple ways to represent the same character. Normalization converts text to a standard form.
                                </Box>
                                <Box variant="strong">Why does this matter?</Box>
                                <Box variant="p">
                                  Different normalized forms can cause security issues when comparing strings, validating input, or bypassing filters.
                                </Box>
                              </SpaceBetween>
                            </Alert>

                            <div>
                              <Box variant="h4">Length Changes</Box>
                              <ColumnLayout columns={2} variant="text-grid">
                                <div>
                                  <Box variant="awsui-key-label">Original Length</Box>
                                  <Box variant="p">{securityAnalysis.normalization.originalLength} characters</Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">NFC Length</Box>
                                  <Box variant="p">
                                    {securityAnalysis.normalization.nfcLength} characters
                                    {securityAnalysis.normalization.nfcLength !== securityAnalysis.normalization.originalLength && (
                                      <Badge color={securityAnalysis.normalization.nfcLength > securityAnalysis.normalization.originalLength ? 'red' : 'blue'}>
                                        {securityAnalysis.normalization.nfcLength > securityAnalysis.normalization.originalLength ? '+' : ''}
                                        {securityAnalysis.normalization.nfcLength - securityAnalysis.normalization.originalLength}
                                      </Badge>
                                    )}
                                  </Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">NFD Length</Box>
                                  <Box variant="p">
                                    {securityAnalysis.normalization.nfdLength} characters
                                    {securityAnalysis.normalization.nfdLength !== securityAnalysis.normalization.originalLength && (
                                      <Badge color={securityAnalysis.normalization.nfdLength > securityAnalysis.normalization.originalLength ? 'red' : 'blue'}>
                                        {securityAnalysis.normalization.nfdLength > securityAnalysis.normalization.originalLength ? '+' : ''}
                                        {securityAnalysis.normalization.nfdLength - securityAnalysis.normalization.originalLength}
                                      </Badge>
                                    )}
                                  </Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">NFKC Length</Box>
                                  <Box variant="p">
                                    {securityAnalysis.normalization.nfkcLength} characters
                                    {securityAnalysis.normalization.nfkcLength !== securityAnalysis.normalization.originalLength && (
                                      <Badge color={securityAnalysis.normalization.nfkcLength > securityAnalysis.normalization.originalLength ? 'red' : 'blue'}>
                                        {securityAnalysis.normalization.nfkcLength > securityAnalysis.normalization.originalLength ? '+' : ''}
                                        {securityAnalysis.normalization.nfkcLength - securityAnalysis.normalization.originalLength}
                                      </Badge>
                                    )}
                                  </Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">NFKD Length</Box>
                                  <Box variant="p">
                                    {securityAnalysis.normalization.nfkdLength} characters
                                    {securityAnalysis.normalization.nfkdLength !== securityAnalysis.normalization.originalLength && (
                                      <Badge color={securityAnalysis.normalization.nfkdLength > securityAnalysis.normalization.originalLength ? 'red' : 'blue'}>
                                        {securityAnalysis.normalization.nfkdLength > securityAnalysis.normalization.originalLength ? '+' : ''}
                                        {securityAnalysis.normalization.nfkdLength - securityAnalysis.normalization.originalLength}
                                      </Badge>
                                    )}
                                  </Box>
                                </div>
                              </ColumnLayout>
                            </div>

                            <div>
                              <Box variant="h4">Changes Detected</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.normalization.changesDetected.map((change, idx) => (
                                  <Box key={idx} variant="p">• {change}</Box>
                                ))}
                              </SpaceBetween>
                            </div>

                            <div>
                              <Box variant="h4">Normalization Forms Explained</Box>
                              <SpaceBetween size="xs">
                                <Box variant="p"><strong>NFC (Canonical Composition):</strong> Combines characters into their composed form (e.g., é as one character)</Box>
                                <Box variant="p"><strong>NFD (Canonical Decomposition):</strong> Splits characters into base + combining marks (e.g., é as e + ´)</Box>
                                <Box variant="p"><strong>NFKC (Compatibility Composition):</strong> Like NFC but also converts similar-looking characters to standard equivalents</Box>
                                <Box variant="p"><strong>NFKD (Compatibility Decomposition):</strong> Like NFD but also decomposes compatibility characters</Box>
                              </SpaceBetween>
                            </div>

                            <Alert type="warning">
                              <SpaceBetween size="xs">
                                <Box variant="strong">Security Implications</Box>
                                <Box variant="p">
                                  • Authentication bypass: Username "admin" vs "admin" (with invisible chars)<br/>
                                  • Filter evasion: Script tags with normalization-sensitive characters<br/>
                                  • Data integrity: Same-looking text producing different hashes<br/>
                                  • Database inconsistency: Duplicate detection failures
                                </Box>
                              </SpaceBetween>
                            </Alert>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* BiDi Exploit */}
                      {securityAnalysis.bidiExploit && (
                        <ExpandableSection headerText={`BiDi Text Direction Exploit (${securityAnalysis.bidiExploit.type}) - ${securityAnalysis.bidiExploit.positions.length} characters`} variant="container">
                          <SpaceBetween size="s">
                            <Alert type="error">
                              Text direction control characters detected! These can be used to disguise malicious code or URLs.
                            </Alert>
                            <div>
                              <Box variant="awsui-key-label">Displayed Text</Box>
                              <Box variant="p"><span style={{ fontFamily: 'monospace' }}>{securityAnalysis.bidiExploit.displayedText}</span></Box>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Actual Text</Box>
                              <Box variant="p"><span style={{ fontFamily: 'monospace' }}>{securityAnalysis.bidiExploit.actualText}</span></Box>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Detected Positions</Box>
                              <Box variant="p">
                                <span style={{ fontFamily: 'monospace' }}>
                                  {securityAnalysis.bidiExploit.positions.slice(0, 10).map((pos, idx) => (
                                    <span key={idx}>
                                      {idx > 0 && ', '}
                                      {renderClickablePosition(pos)}
                                    </span>
                                  ))}
                                  {securityAnalysis.bidiExploit.positions.length > 10 && ` ... +${securityAnalysis.bidiExploit.positions.length - 10} more`}
                                </span>
                              </Box>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Homoglyph Detection */}
                      {securityAnalysis.homoglyphs && securityAnalysis.homoglyphs.length > 0 && (
                        <ExpandableSection headerText={`Homoglyphs (${securityAnalysis.homoglyphs.length} detected)`} variant="container">
                          <SpaceBetween size="s">
                            <div>
                              <Box variant="awsui-key-label">Detected Homoglyphs</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.homoglyphs.slice(0, 20).map((homoglyph, index) => (
                                  <Box key={index} variant="p">
                                    {renderClickablePosition(homoglyph.position)}: <span style={{ fontFamily: 'monospace', fontSize: '16px' }}>{homoglyph.character}</span> looks like <span style={{ fontFamily: 'monospace' }}>{homoglyph.lookalike}</span> (Script: {homoglyph.script})
                                  </Box>
                                ))}
                                {securityAnalysis.homoglyphs.length > 20 && (
                                  <Box variant="p">... and {securityAnalysis.homoglyphs.length - 20} more</Box>
                                )}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Script Mixing */}
                      {securityAnalysis.scriptMixing && securityAnalysis.scriptMixing.isSuspicious && (
                        <ExpandableSection headerText={`Script Mixing (${securityAnalysis.scriptMixing.scripts.length} scripts)`} variant="container">
                          <SpaceBetween size="s">
                            <div>
                              <Box variant="awsui-key-label">Scripts Found</Box>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {securityAnalysis.scriptMixing.scripts.map((script, idx) => (
                                  <Badge key={idx} color="blue">{script}</Badge>
                                ))}
                              </div>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Whitespace Abuse */}
                      {securityAnalysis.whitespaceAbuse && securityAnalysis.whitespaceAbuse.hasAbuse && (
                        <ExpandableSection headerText={`Whitespace Abuse (${securityAnalysis.whitespaceAbuse.issues.length} issue${securityAnalysis.whitespaceAbuse.issues.length > 1 ? 's' : ''})`} variant="container">
                          <SpaceBetween size="s">
                            {securityAnalysis.whitespaceAbuse.issues.map((issue, index) => (
                              <div key={index} style={{ padding: '8px', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
                                <SpaceBetween size="xs">
                                  <Box variant="p"><strong>Type:</strong> {issue.type.replace(/_/g, ' ').toUpperCase()}</Box>
                                  <Box variant="p"><strong>Description:</strong> {issue.description}</Box>
                                  {issue.spaceTypes.length > 0 && (
                                    <Box variant="p"><strong>Space Types:</strong> {issue.spaceTypes.join(', ')}</Box>
                                  )}
                                  <div>
                                    <Box variant="awsui-key-label">Affected Positions ({issue.positions.length})</Box>
                                    <Box variant="p">
                                      <span style={{ fontFamily: 'monospace' }}>
                                        {issue.positions.slice(0, 10).map((pos, idx) => (
                                          <span key={idx}>
                                            {idx > 0 && ', '}
                                            {renderClickablePosition(pos)}
                                          </span>
                                        ))}
                                        {issue.positions.length > 10 && ` ... +${issue.positions.length - 10} more`}
                                      </span>
                                    </Box>
                                  </div>
                                </SpaceBetween>
                              </div>
                            ))}
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Character Width Anomalies */}
                      {securityAnalysis.widthAbuse && securityAnalysis.widthAbuse.hasAbuse && (
                        <ExpandableSection headerText={`Character Width Anomalies (${securityAnalysis.widthAbuse.anomalies.length} detected)`} variant="container">
                          <SpaceBetween size="s">
                            <Alert type={securityAnalysis.widthAbuse.mixedWidths ? 'warning' : 'info'}>
                              {securityAnalysis.widthAbuse.mixedWidths 
                                ? 'Full-width characters mixed with normal ASCII detected - HIGH risk for spoofing!' 
                                : 'Full-width or half-width characters detected.'}
                            </Alert>
                            <div>
                              <Box variant="awsui-key-label">Detected Anomalies</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.widthAbuse.anomalies.slice(0, 20).map((anomaly, index) => (
                                  <Box key={index} variant="p">
                                    {renderClickablePosition(anomaly.position)}: <span style={{ fontFamily: 'monospace', fontSize: '16px' }}>{anomaly.character}</span> ({anomaly.width}) → <span style={{ fontFamily: 'monospace' }}>{anomaly.normalEquivalent}</span>
                                  </Box>
                                ))}
                                {securityAnalysis.widthAbuse.anomalies.length > 20 && (
                                  <Box variant="p">... and {securityAnalysis.widthAbuse.anomalies.length - 20} more</Box>
                                )}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Look-alike Digits */}
                      {securityAnalysis.lookalikeDigits && securityAnalysis.lookalikeDigits.hasAbuse && (
                        <ExpandableSection headerText={`Look-alike Digits (${securityAnalysis.lookalikeDigits.digits.length} detected)`} variant="container">
                          <SpaceBetween size="s">
                            <div>
                              <Box variant="awsui-key-label">Digit Types Found</Box>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {[...new Set(securityAnalysis.lookalikeDigits.digits.map(d => d.digitType))].map((type, idx) => (
                                  <Badge key={idx} color="blue">{type.replace(/_/g, ' ')}</Badge>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Detected Digits</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.lookalikeDigits.digits.slice(0, 20).map((digit, index) => (
                                  <Box key={index} variant="p">
                                    {renderClickablePosition(digit.position)}: <span style={{ fontFamily: 'monospace', fontSize: '16px' }}>{digit.character}</span> ({digit.digitType}) → {digit.normalEquivalent}
                                  </Box>
                                ))}
                                {securityAnalysis.lookalikeDigits.digits.length > 20 && (
                                  <Box variant="p">... and {securityAnalysis.lookalikeDigits.digits.length - 20} more</Box>
                                )}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Quotation Mark Confusion */}
                      {securityAnalysis.quotationConfusion && securityAnalysis.quotationConfusion.hasConfusion && (
                        <ExpandableSection headerText={`Quotation Mark Confusion (${securityAnalysis.quotationConfusion.anomalies.length} detected)`} variant="container">
                          <SpaceBetween size="s">
                            {securityAnalysis.quotationConfusion.potentialSQLInjection && (
                              <Alert type="error">
                                <strong>⚠️ POTENTIAL SQL INJECTION DETECTED!</strong><br/>
                                Non-standard quotes detected alongside SQL keywords. This may be an attempt to bypass SQL injection filters.
                              </Alert>
                            )}
                            <div>
                              <Box variant="awsui-key-label">Quote Types Found</Box>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                {[...new Set(securityAnalysis.quotationConfusion.anomalies.map(a => a.type))].map((type, idx) => (
                                  <Badge key={idx} color={securityAnalysis.quotationConfusion?.potentialSQLInjection ? 'red' : 'blue'}>{type.replace(/_/g, ' ')}</Badge>
                                ))}
                              </div>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Detected Quotations</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.quotationConfusion.anomalies.slice(0, 20).map((anomaly, index) => (
                                  <Box key={index} variant="p">
                                    {renderClickablePosition(anomaly.position)}: <span style={{ fontFamily: 'monospace', fontSize: '16px' }}>{anomaly.character}</span> ({anomaly.type}) → <span style={{ fontFamily: 'monospace' }}>{anomaly.standardEquivalent}</span>
                                  </Box>
                                ))}
                                {securityAnalysis.quotationConfusion.anomalies.length > 20 && (
                                  <Box variant="p">... and {securityAnalysis.quotationConfusion.anomalies.length - 20} more</Box>
                                )}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}
                      {/* Repeating Pattern */}
                      {securityAnalysis.repeatingPattern && (
                        <ExpandableSection headerText="Repeating Invisible Character Pattern" variant="container">
                          <SpaceBetween size="s">
                            <ColumnLayout columns={3} variant="text-grid">
                              <div>
                                <Box variant="awsui-key-label">Interval</Box>
                                <Box variant="p">Every ~{securityAnalysis.repeatingPattern.interval} positions</Box>
                              </div>
                              <div>
                                <Box variant="awsui-key-label">Confidence</Box>
                                <Box variant="p">{securityAnalysis.repeatingPattern.confidence}%</Box>
                              </div>
                              <div>
                                <Box variant="awsui-key-label">Occurrences</Box>
                                <Box variant="p">{securityAnalysis.repeatingPattern.positions.length}</Box>
                              </div>
                            </ColumnLayout>
                            <div>
                              <Box variant="awsui-key-label">Description</Box>
                              <Box variant="p">{securityAnalysis.repeatingPattern.description}</Box>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Positional Pattern */}
                      {securityAnalysis.positionalPattern && securityAnalysis.positionalPattern.distribution !== 'uniform' && (
                        <ExpandableSection headerText="Positional Distribution Pattern" variant="container">
                          <SpaceBetween size="s">
                            <ColumnLayout columns={3} variant="text-grid">
                              <div>
                                <Box variant="awsui-key-label">Distribution Type</Box>
                                <Badge color={
                                  securityAnalysis.positionalPattern.distribution === 'start' || 
                                  securityAnalysis.positionalPattern.distribution === 'end' ? 'red' : 'blue'
                                }>
                                  {securityAnalysis.positionalPattern.distribution.toUpperCase()}
                                </Badge>
                              </div>
                              <div>
                                <Box variant="awsui-key-label">Start Clustering</Box>
                                <Box variant="p">{securityAnalysis.positionalPattern.startClustering ? 'Yes' : 'No'}</Box>
                              </div>
                              <div>
                                <Box variant="awsui-key-label">End Clustering</Box>
                                <Box variant="p">{securityAnalysis.positionalPattern.endClustering ? 'Yes' : 'No'}</Box>
                              </div>
                            </ColumnLayout>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Diacritics Abuse */}
                      {securityAnalysis.diacriticsAbuse && securityAnalysis.diacriticsAbuse.hasAbuse && (
                        <ExpandableSection headerText={`Combining Diacritics Abuse (${securityAnalysis.diacriticsAbuse.affectedPositions.length} affected)`} variant="container">
                          <SpaceBetween size="s">
                            <div>
                              <Box variant="awsui-key-label">Maximum Diacritics on Single Character</Box>
                              <Box variant="p">{securityAnalysis.diacriticsAbuse.maxDiacriticsOnChar}</Box>
                            </div>
                            <div>
                              <Box variant="awsui-key-label">Affected Characters</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.diacriticsAbuse.affectedPositions.map((affected, index: number) => (
                                  <Box key={index} variant="p">
                                    {renderClickablePosition(affected.position)}: '{affected.baseChar}' has {affected.diacriticCount} combining marks
                                  </Box>
                                ))}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Format Character Abuse */}
                      {securityAnalysis.formatAbuse && securityAnalysis.formatAbuse.hasAbuse && (
                        <ExpandableSection headerText={`Format Character Abuse (${securityAnalysis.formatAbuse.positions.length} occurrences)`} variant="container">
                          <SpaceBetween size="s">
                            <ColumnLayout columns={3} variant="text-grid">
                              <div>
                                <Box variant="awsui-key-label">BOMs</Box>
                                <Box variant="p">{securityAnalysis.formatAbuse.bomCount}</Box>
                              </div>
                              <div>
                                <Box variant="awsui-key-label">Soft Hyphens</Box>
                                <Box variant="p">{securityAnalysis.formatAbuse.softHyphenCount}</Box>
                              </div>
                              <div>
                                <Box variant="awsui-key-label">Separators</Box>
                                <Box variant="p">{securityAnalysis.formatAbuse.separatorCount}</Box>
                              </div>
                            </ColumnLayout>
                            <div>
                              <Box variant="awsui-key-label">Positions</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.formatAbuse.positions.slice(0, 10).map((pos, index: number) => (
                                  <Box key={index} variant="p">
                                    {renderClickablePosition(pos.position)}: {pos.type}
                                  </Box>
                                ))}
                                {securityAnalysis.formatAbuse.positions.length > 10 && (
                                  <Box variant="p">... and {securityAnalysis.formatAbuse.positions.length - 10} more</Box>
                                )}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Semantic Context Analysis */}
                      {securityAnalysis.semanticContext && securityAnalysis.semanticContext.affectedWords && securityAnalysis.semanticContext.affectedWords.length > 0 && (
                        <ExpandableSection headerText={`Semantic Context Analysis (${securityAnalysis.semanticContext.affectedWords.length} words)`} variant="container">
                          <SpaceBetween size="s">
                            <Alert type="warning">
                              {securityAnalysis.semanticContext.description}
                            </Alert>
                            <div>
                              <Box variant="awsui-key-label">Affected Words</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.semanticContext.affectedWords.map((word, index: number) => {
                                  // Extract excerpt from original text (20 chars before and after)
                                  const startPos = Math.max(0, word.position - 20);
                                  const endPos = Math.min(text.length, word.position + word.word.length + 20);
                                  const excerpt = text.substring(startPos, endPos);
                                  const excerptStart = startPos > 0 ? '...' : '';
                                  const excerptEnd = endPos < text.length ? '...' : '';
                                  
                                  return (
                                    <div key={index} style={{ marginBottom: '12px', padding: '8px', backgroundColor: '#f9f9f9', borderRadius: '4px' }}>
                                      <Box variant="p" margin={{ bottom: 'xs' }}>
                                        <strong>{renderClickablePosition(word.position)}:</strong> <span style={{ fontFamily: 'monospace', backgroundColor: '#fff3cd', padding: '2px 4px', borderRadius: '2px' }}>{word.word}</span>
                                        {' (Split at: '}
                                        <span style={{ fontFamily: 'monospace' }}>{word.splitPositions.join(', ')}</span>
                                        {')'}
                                      </Box>
                                      <Box margin={{ top: 'xs' }}>
                                        <Box variant="awsui-key-label" fontSize="body-s">Context Excerpt:</Box>
                                        <div style={{ 
                                          fontFamily: 'monospace', 
                                          fontSize: '12px', 
                                          padding: '8px', 
                                          backgroundColor: '#ffffff', 
                                          border: '1px solid #d5dbdb',
                                          borderRadius: '4px',
                                          marginTop: '4px',
                                          overflowX: 'auto'
                                        }}>
                                          {excerptStart}{excerpt}{excerptEnd}
                                        </div>
                                      </Box>
                                    </div>
                                  );
                                })}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* URL/Domain Confusables */}
                      {securityAnalysis.urlConfusables && securityAnalysis.urlConfusables.length > 0 && (
                        <ExpandableSection headerText={`URL/Domain Confusables (${securityAnalysis.urlConfusables.length} detected)`} variant="container">
                          <SpaceBetween size="s">
                            {securityAnalysis.urlConfusables.map((urlConf, index: number) => (
                              <Alert key={index} type="error" header={`Suspicious URL: ${urlConf.url}`}>
                                <SpaceBetween size="xs">
                                  <Box variant="p"><strong>Position:</strong> {renderClickablePosition(urlConf.position)}</Box>
                                  {urlConf.issues && urlConf.issues.map((issue, issueIdx: number) => (
                                    <Box key={issueIdx} variant="p">
                                      <Badge color="red">{issue.type.replace('_', ' ').toUpperCase()}</Badge>
                                      {' '}{issue.details}
                                    </Box>
                                  ))}
                                </SpaceBetween>
                              </Alert>
                            ))}
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Zero-Width Character Chain */}
                      {securityAnalysis.zwChain && securityAnalysis.zwChain.chainLength > 0 && (
                        <ExpandableSection headerText={`Zero-Width Character Chain (${securityAnalysis.zwChain.chainLength} chars)`} variant="container">
                          <SpaceBetween size="s">
                            <Alert type="error">
                              A sequence of {securityAnalysis.zwChain.chainLength} consecutive zero-width characters detected. This may be used to hide data or bypass filters.
                            </Alert>
                            <ColumnLayout columns={2} variant="text-grid">
                              <div>
                                <Box variant="awsui-key-label">Chain Start Position</Box>
                                <Box variant="p">{securityAnalysis.zwChain.positions.length > 0 && renderClickablePosition(securityAnalysis.zwChain.positions[0])}</Box>
                              </div>
                              <div>
                                <Box variant="awsui-key-label">Chain Length</Box>
                                <Box variant="p">{securityAnalysis.zwChain.chainLength} characters</Box>
                              </div>
                            </ColumnLayout>
                            {securityAnalysis.zwChain.pattern && (
                              <div>
                                <Box variant="awsui-key-label">Character Pattern</Box>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
                                  {securityAnalysis.zwChain.pattern.split('+').map((type: string, idx: number) => (
                                    <Badge key={idx} color="red">{type}</Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Language-Specific Character Attacks */}
                      {securityAnalysis.languageIssues && securityAnalysis.languageIssues.length > 0 && (
                        <ExpandableSection 
                          headerText={`Language-Specific Character Attacks - ${securityAnalysis.languageIssues.length} detected`}
                          variant="container"
                        >
                          <SpaceBetween size="s">
                            <div>
                              <Box variant="awsui-key-label">Detected Language Issues</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.languageIssues.map((issue, idx) => (
                                  <Box variant="p" key={idx} fontSize="body-s">
                                    <strong>{issue.positions.length} positions:</strong> {issue.description}
                                  </Box>
                                ))}
                                {securityAnalysis.languageIssues.length > 10 && (
                                  <Box variant="p">... and {securityAnalysis.languageIssues.length - 10} more</Box>
                                )}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Mathematical Symbol Substitution */}
                      {securityAnalysis.mathSubstitutions && securityAnalysis.mathSubstitutions.length > 0 && (
                        <ExpandableSection 
                          headerText={`Mathematical Symbol Substitution - ${securityAnalysis.mathSubstitutions.length} detected`}
                          variant="container"
                        >
                          <SpaceBetween size="s">
                            <Alert type="info">
                              Mathematical symbols used to replace regular characters. This can bypass filters that check for specific character patterns.
                            </Alert>
                            <div>
                              <Box variant="awsui-key-label">Detected Substitutions</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.mathSubstitutions?.map((subst, idx) => (
                                  <Box variant="p" key={idx} fontSize="body-s">
                                    Character substitution detected at <strong>{subst.positions.length} positions</strong>
                                  </Box>
                                ))}
                                {securityAnalysis.mathSubstitutions.length > 10 && (
                                  <Box variant="p">... and {securityAnalysis.mathSubstitutions.length - 10} more</Box>
                                )}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* Domain-Specific Pattern Detection */}
                      {securityAnalysis.domainAbuse && securityAnalysis.domainAbuse.patterns && securityAnalysis.domainAbuse.patterns.length > 0 && (
                        <ExpandableSection 
                          headerText={`Domain-Specific Pattern Detection - ${securityAnalysis.domainAbuse.patterns.length} detected`}
                          variant="container"
                        >
                          <SpaceBetween size="s">
                            <Alert type="info">
                              Domain-specific suspicious patterns detected that may be used for phishing or evasion.
                            </Alert>
                            <div>
                              <Box variant="awsui-key-label">Detected Patterns</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.domainAbuse.patterns.map((pattern, idx) => (
                                  <Box key={idx} variant="p" fontSize="body-s">
                                    {pattern.type}: {pattern.value}
                                  </Box>
                                ))}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}

                      {/* String Length Manipulation */}
                      {securityAnalysis.lengthAbuse && securityAnalysis.lengthAbuse.discrepancies && securityAnalysis.lengthAbuse.discrepancies.length > 0 && (
                        <ExpandableSection 
                          headerText={`String Length Manipulation - ${securityAnalysis.lengthAbuse.discrepancies.length} detected`}
                          variant="container"
                        >
                          <SpaceBetween size="s">
                            <Alert type="warning">
                              String length discrepancies detected. Text may contain invisible characters that affect length calculations without visible changes.
                            </Alert>
                            <div>
                              <Box variant="awsui-key-label">Detected Length Discrepancies</Box>
                              <SpaceBetween size="xs">
                                {securityAnalysis.lengthAbuse.discrepancies.map((disc, idx) => (
                                  <Box key={idx} variant="p" fontSize="body-s">
                                    Visible length: <strong>{disc.visibleLength}</strong>, Code points: <strong>{disc.codePointLength}</strong>, Bytes: <strong>{disc.byteLength}</strong>
                                  </Box>
                                ))}
                              </SpaceBetween>
                            </div>
                          </SpaceBetween>
                        </ExpandableSection>
                      )}
                    </SpaceBetween>
                  </SpaceBetween>
                )}

                {/* Detected Characters */}
                <div style={{ borderBottom: '2px solid #388e3c', paddingBottom: '8px', marginBottom: '16px' }}>
                  <Box variant="h2">
                    Detected Characters
                  </Box>
                </div>
                {statistics.charGroups.length > 0 && (
                  <SpaceBetween size="xs">
                    <SpaceBetween size="xs">
                      {statistics.charGroups.map((group, index) => {
                        const char = group.char.character;
                        const isExpanded = index === 0;
                        
                        return (
                          <ExpandableSection
                            key={char.code}
                            defaultExpanded={isExpanded}
                            variant="container"
                            headerText={
                              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%' }}>
                                <div
                                  style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: '6px',
                                    backgroundColor: 
                                      getCharacterBadgeColor(char.category) === 'green' ? '#e8f5e9' :
                                      getCharacterBadgeColor(char.category) === 'red' ? '#ffebee' :
                                      getCharacterBadgeColor(char.category) === 'blue' ? '#e3f2fd' : '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    color:
                                      getCharacterBadgeColor(char.category) === 'green' ? '#2e7d32' :
                                      getCharacterBadgeColor(char.category) === 'red' ? '#c62828' :
                                      getCharacterBadgeColor(char.category) === 'blue' ? '#1565c0' : '#424242',
                                    border: `2px solid ${
                                      getCharacterBadgeColor(char.category) === 'green' ? '#4caf50' :
                                      getCharacterBadgeColor(char.category) === 'red' ? '#f44336' :
                                      getCharacterBadgeColor(char.category) === 'blue' ? '#2196f3' : '#9e9e9e'
                                    }`,
                                  }}
                                >
                                  {char.symbol}
                                </div>
                                <div style={{ flex: 1 }}>
                                  <Box variant="strong">{char.name}</Box>
                                </div>
                                <Badge color={getCharacterBadgeColor(char.category)}>
                                  {group.count}
                                </Badge>
                              </div>
                            }
                          >
                            <SpaceBetween size="s">
                              <ColumnLayout columns={2} variant="text-grid">
                                <div>
                                  <Box variant="awsui-key-label">Unicode:</Box>
                                  <Box variant="p">{formatUnicodeCode(char.code)}</Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Description:</Box>
                                  <Box variant="p">{char.name}</Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">HTML:</Box>
                                  <Box variant="p">
                                    <span style={{ fontFamily: 'monospace' }}>
                                      &amp;#{char.code};
                                    </span>
                                  </Box>
                                </div>
                                <div>
                                  <Box variant="awsui-key-label">Windows Typing:</Box>
                                  <Box variant="p">
                                    <span style={{ fontFamily: 'monospace' }}>
                                      Alt++{char.code.toString(16).toUpperCase()}
                                    </span>
                                  </Box>
                                </div>
                                {onCharacterFix && (
                                  <div>
                                    <Box variant="awsui-key-label">Fix Will:</Box>
                                    <Box variant="p">Remove</Box>
                                  </div>
                                )}
                                <div style={{ gridColumn: '1 / -1' }}>
                                  <Box variant="awsui-key-label">
                                    Positions ({group.positions.length} occurrences)
                                  </Box>
                                  <Box variant="p">
                                    <span style={{ fontFamily: 'monospace' }}>
                                      {group.positions.slice(0, 10).map((pos: number, idx: number) => (
                                        <span key={idx}>
                                          {idx > 0 && ', '}
                                          {renderClickablePosition(pos)}
                                        </span>
                                      ))}
                                      {group.positions.length > 10 && ` ... +${group.positions.length - 10} more`}
                                    </span>
                                  </Box>
                                </div>
                              </ColumnLayout>

                              <SpaceBetween direction="horizontal" size="xs">
                                {onCharacterFix && (
                                  <Button
                                    iconName="remove"
                                    onClick={() => handleFixCharacter(char.code)}
                                    variant="primary"
                                  >
                                    Fix Character
                                  </Button>
                                )}
                                {allowCopyCharacter && (
                                  <Button
                                    iconName="copy"
                                    onClick={() => handleCopyCharacter(char.code)}
                                  >
                                    Copy Character
                                  </Button>
                                )}
                              </SpaceBetween>
                            </SpaceBetween>
                          </ExpandableSection>
                        );
                      })}
                    </SpaceBetween>
                  </SpaceBetween>
                )}
              </SpaceBetween>
            ) : (
              <Alert type="success">
                Your text is clean - no invisible characters detected.
              </Alert>
            )}
          </SpaceBetween>
        </div>
      </ColumnLayout>
    </Container>
  );
};

